import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Model, StylesManager } from "survey-core";
import { Survey } from "survey-react-ui";
import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import MessageScreen from "components/base/molecules/MessageScreen";
import SurveyAlert from "components/base/molecules/SurveyAlert";
import { SurveyRoutes, maxCompensation } from "config/Consts";
import { Prompt, useHistory, useLocation, useParams } from "react-router-dom";
import "survey-core/defaultV2.css";
import {
  surveyCompletedState,
  surveySavingAlertState,
} from "_state/atoms/AppAtoms";
import { useRecoilState, useRecoilValue } from "recoil";
import "./survey.scss";
import { SecondaryButton } from "components/base/atoms/HJSButton";
import { shuffleSurvey } from "utils/getSurvey";
import { useGetSurveysInfoQuery, useGetUserProfileQuery } from "queries";
import AddressFormModal from "components/forms/AddressForm";

StylesManager.applyTheme("defaultV2");

interface ISurveyComponent {
  id: string;
  submitHandler: (arg?: any) => void;
  isLoading: boolean;
  surveyResult?: any;
  maxDuration?: number;
  reward?: number;
  savedIndex?: any;
  surveyJson?: any;
}

const setPages = (pagesDict: any, originalPages: any[]): any[] => {
  const dictKeys = Object.keys(pagesDict);
  const result = new Array(dictKeys.length);

  originalPages?.forEach((page: any, idx: number) => {
    const index = pagesDict[page.name];
    result[index] = page;
  });

  return result;
};

const SurveyComponent: FC<ISurveyComponent> = ({
  id,
  submitHandler,
  isLoading,
  surveyResult,
  savedIndex,
  reward,
  surveyJson: externalSurveyJson,
  // durationPerPage,
}): JSX.Element => {
  const { name } = useParams<{ name?: string }>();
  const location = useLocation();
  const history = useHistory();
  // let t: any;

  const isSurveyCompleted = useRecoilValue(surveyCompletedState);
  const [alertState, setAlertState] = useRecoilState(surveySavingAlertState);
  const [surveyJson, setSurveyJson] = useState<any>();
  const [showSurvey, setShowSurvey] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const handleClose = (): void => {
    setOpen(false);
  };

  const {
    data: surveysInfo,
    refetch: refetchSurveys,
    dataUpdatedAt,
  } = useGetSurveysInfoQuery();
  const { data: userDetails, refetch: refetchProfile } =
    useGetUserProfileQuery();

  const surveyPagesDict: any = useMemo(
    () => ({ ...savedIndex } ?? {}),
    [surveyResult, id]
  );

  // create survey instance according to the savedIndex
  // when survey questions randomized
  const survey = useMemo(() => {
    if ((savedIndex as boolean) && (surveyJson as boolean)) {
      const { pages, ...restSurvey } = surveyJson;
      const surveyOldOrder = setPages(savedIndex, pages);
      const newSurvey = {
        pages: surveyOldOrder,
        ...restSurvey,
      };
      return new Model(newSurvey);
    }
    if (surveyJson as boolean) return new Model(surveyJson);

    // if external survey JSON provided (from backend), use it directly
    if (externalSurveyJson as boolean) return new Model(externalSurveyJson);

    return new Model({ surveyId: id });
  }, [surveyJson, id, savedIndex, externalSurveyJson]);

  survey.showTimerPanel = "none";
  survey.showProgressBar = "bottom";
  survey.showQuestionNumbers = "off";

  const durationPerPage: any = useMemo(() => ({}), [survey]);

  const surveyCompleteHandler = useCallback(
    (surveyData: any) => {
      const { pageNo, ...surveyResult } = surveyData.data;
      if (location.pathname === SurveyRoutes.elgiblity) {
        surveyResult.participantZIP = surveyResult.participantZIP?.toString();
        surveyResult.participantAge = surveyResult.participantAge?.toString();
      }
      submitHandler({
        data: surveyResult,
        durationPerPage,
      });
    },
    [id, survey]
  );

  // save response in between (save and quit functionality)
  const savePartialData = (): void => {
    submitHandler({
      data: { ...survey.data, currentPageNo: survey.currentPageNo },
      isCompleted: false,
      savedIndex: surveyPagesDict,
      durationPerPage,
    });
  };

  useEffect(() => {
    if (externalSurveyJson as boolean) {
      !location?.pathname?.includes(SurveyRoutes.elgiblity) &&
        (survey.firstPageIsStarted = true);
      survey.startTimer();
      survey.onComplete.add((surveyObj: any) => {
        durationPerPage[surveyObj.currentPage.propertyHash.name] =
          surveyObj.activePage.timeSpent;
        surveyCompleteHandler(surveyObj);
      });
      survey.sendResultOnPageNext = true;
      setShowSurvey(true);
      setTimeout(() => window.scroll(0, 0), 100);
    }
  }, [externalSurveyJson, survey]);

  useEffect(() => {
    survey.onLoadedSurveyFromService.add(() => {
      !location?.pathname?.includes(SurveyRoutes.elgiblity) &&
        (survey.firstPageIsStarted = true);
      survey.startTimer();
      if (
        !(surveyJson as boolean) &&
        location.pathname === SurveyRoutes.personality
      ) {
        const randomSurvey =
          Object.keys(surveyPagesDict).length > 0
            ? survey.toJSON().pages
            : shuffleSurvey(survey.toJSON().pages, surveyPagesDict);

        setSurveyJson({
          pages: randomSurvey,
        });

        setShowSurvey(true);
      } else {
        setShowSurvey(true);
      }
      setTimeout(() => window.scroll(0, 0), 100);
    });

    // attach handler for completing the survey
    if (id?.length > 0) {
      survey.onComplete.add((surveyObj: any) => {
        durationPerPage[surveyObj.currentPage.propertyHash.name] =
          surveyObj.activePage.timeSpent;
        surveyCompleteHandler(surveyObj);
      });
      survey.sendResultOnPageNext = true;
    }
  }, [id, survey]);

  useEffect(() => {
    // set received data to the survey
    if (surveyResult as boolean) {
      const { currentPageNo, ...data } = surveyResult;
      survey.data = data;
    }

    // update the survey page to the saved page
    if (survey?.currentPageNo < surveyResult?.currentPageNo)
      survey.currentPageNo = surveyResult.currentPageNo;
  }, [surveyResult, survey]);

  // alert before reloading page
  useEffect(() => {
    const unloadCallback = (e: any): string => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    if (!isSurveyCompleted)
      window.addEventListener("beforeunload", unloadCallback);
    return () => window.removeEventListener("beforeunload", unloadCallback);
  }, []);

  useEffect(() => {
    if (survey !== undefined || survey !== null) {
      survey.stopTimer();
      survey.startTimer();
    }
  }, [survey]);

  survey.showPrevButton = false;
  survey.focusFirstQuestionAutomatic = false;
  survey.showCompletedPage = false;
  useEffect(() => {
    refetchSurveys();
    refetchProfile();
  }, [location]);

  useEffect(() => {
    if (
      surveysInfo?.[0]?.isCompleted === true &&
      userDetails?.googleAddress === false &&
      userDetails?.address === false
    ) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [surveysInfo, userDetails, dataUpdatedAt, location]);

  return (
    <>
      <SurveyAlert
        isOpen={alertState?.isOpen}
        onClose={() => setAlertState({ isOpen: false })}
        confirmHandler={savePartialData}
        isLoading={isLoading}
      />
      <AddressFormModal open={open} handleClose={handleClose} />

      <Prompt
        when={
          location.pathname.includes("/survey") &&
          !alertState?.isOpen &&
          !isSurveyCompleted &&
          !isLoading
        }
        message="Are you sure you want to leave the page? Changes that you made may not be saved."
      />

      {isSurveyCompleted ? (
        surveysInfo?.[2]?.isCompleted === true &&
        surveysInfo?.[2]?.layer === 2 ? (
          <MessageScreen
            // eslint-disable-next-line no-template-curly-in-string
            message={
              <>
                <Text className="font-normal">
                  You earned ${reward?.toString()} for your time!{" "}
                  <span className="font-bold text-red-500">
                    The last step before we can process your check is for you to
                    visit the job search page:
                  </span>
                </Text>

                <SecondaryButton
                  className="my-4 mt-8"
                  onClick={() => history.push("/jobs/search")}
                >
                  Apply for Jobs
                </SecondaryButton>

                <Text className="font-normal">
                  We&apos;ll process your check as soon as you visit the job
                  search page, and you should get your check within 14 days.
                  Your participation in this study&apos;s surveys has finished!
                  Please remember you can continue to use the Job Search page to
                  enhance your job search.
                </Text>
              </>
            }
            alt="follow3"
            src="https://assets8.lottiefiles.com/packages/lf20_xrbvwh6y.json"
          />
        ) : (
          <MessageScreen
            // eslint-disable-next-line no-template-curly-in-string
            message={
              <>
                <Text className="font-normal">
                  You earned ${reward?.toString()} for your time!{" "}
                  <span className="font-bold text-red-500">
                    The last step before we can process your check is for you to
                    visit the job search page:
                  </span>
                </Text>

                <SecondaryButton
                  className="my-4 mt-8"
                  onClick={() => history.push("/jobs/search")}
                >
                  Apply for Jobs
                </SecondaryButton>
                <Text className="font-normal">
                  We’ll process your check as soon as you visit the job search
                  page and you should get your check within 14 business days.
                </Text>

                <Text className="font-normal my-4 mt-8">
                  If you continue to use Job Search page and take additional
                  surveys to share how your job search is going, you can earn an
                  additional ${maxCompensation - userDetails?.amountEarned}!
                </Text>
              </>
            }
            alt="Eligible"
            src="https://assets8.lottiefiles.com/packages/lf20_xrbvwh6y.json"
          />
        )
      ) : (
        <Box px={[2, 2, 12, 12, 12]}>
          {!showSurvey && <Text>Loading Survey ....</Text>}
          {id?.length > 0 && showSurvey && (
            <Survey
              key={name}
              model={survey}
              onCurrentPageChanged={(surveyObj: any) => {
                survey.currentPageNo = surveyObj.currentPageNo;

                survey.stopTimer();
                survey.startTimer();
              }}
              onStarted={() => {
                if (survey.currentPageNo < surveyResult?.currentPageNo) {
                  survey.currentPageNo = surveyResult.currentPageNo;
                }
              }}
              onCurrentPageChanging={(surveyObj: any) => {
                durationPerPage[surveyObj.currentPage.propertyHash.name] =
                  surveyObj.activePage.timeSpent;
              }}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default React.memo(SurveyComponent);
